import { Loader2, Send, Settings, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { MiniMarkdown } from "@/components/MiniMarkdown";

import {
  useAzioneRegola,
  useMemoriaChat,
  useMemoriaEntita,
  useMemoriaRegole,
} from "@/hooks/useMemoria";
import { cn } from "@/lib/utils";
import type { EntitaMemoria, MemoriaRegola, ScopeTipo } from "@/types/memoria";

const SCOPES: { key: ScopeTipo; label: string }[] = [
  { key: "cliente", label: "Clienti" },
  { key: "articolo", label: "Articoli" },
  { key: "commessa", label: "Commesse" },
  { key: "carico", label: "Carichi" },
];

const STATO_LABEL: Record<string, string> = {
  proposta: "Da confermare",
  attiva: "Attiva",
  sospesa: "Sospesa",
  scaduta: "Scaduta",
  revocata: "Revocata",
  superseded: "Storica",
};

function validitaLabel(r: MemoriaRegola): string {
  if (r.valido_al) return `Fino al ${r.valido_al}`;
  return "Permanente";
}

export function MemoriaPage() {
  const [scope, setScope] = useState<ScopeTipo>("cliente");
  const [aperta, setAperta] = useState<EntitaMemoria | null>(null);
  const [filtro, setFiltro] = useState("");
  const entita = useMemoriaEntita(scope);

  const q = filtro.trim().toLowerCase();
  const righe = (entita.data ?? []).filter(
    (e) =>
      !q ||
      e.etichetta.toLowerCase().includes(q) ||
      (e.sottotitolo ?? "").toLowerCase().includes(q)
  );

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Memoria</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Clienti, articoli e commesse. Apri una voce per vedere e gestire le regole che gli
            agenti ricordano e applicano.
          </p>
        </div>
        <Link
          to="/memoria/tipi"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <Settings className="size-4" /> Tipi e permessi
        </Link>
      </header>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg bg-secondary p-1 text-sm">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                setScope(s.key);
                setAperta(null);
                setFiltro("");
              }}
              className={cn(
                "rounded-md px-4 py-1.5 font-medium transition-colors",
                scope === s.key ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
        <input
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          placeholder="Cerca…"
          className="w-64 rounded-md border bg-muted px-3 py-1.5 text-sm focus:bg-background"
        />
      </div>

      <div className="overflow-hidden rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium">Voce</th>
              <th className="px-4 py-2.5 text-left font-medium">Riferimento</th>
              <th className="px-4 py-2.5 text-right font-medium">Regole</th>
            </tr>
          </thead>
          <tbody>
            {entita.isLoading ? (
              <tr>
                <td colSpan={3} className="px-4 py-10 text-center text-muted-foreground">
                  <Loader2 className="mx-auto size-5 animate-spin" />
                </td>
              </tr>
            ) : righe.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-10 text-center text-muted-foreground">
                  Nessuna voce.
                </td>
              </tr>
            ) : (
              righe.slice(0, 300).map((e) => (
                <tr
                  key={e.scope_ref}
                  onClick={() => setAperta(e)}
                  className="cursor-pointer border-t hover:bg-muted/50"
                >
                  <td className="px-4 py-3 font-medium">{e.etichetta}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.sottotitolo}</td>
                  <td className="px-4 py-3 text-right">
                    {e.n_regole > 0 ? (
                      <span className="inline-flex rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">
                        {e.n_regole}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {righe.length > 300 && (
        <p className="mt-2 text-xs text-muted-foreground">
          Mostro le prime 300 di {righe.length} voci — usa la ricerca per restringere.
        </p>
      )}

      {aperta && (
        <MemoriaDrawer scope={scope} entita={aperta} onClose={() => setAperta(null)} />
      )}
    </div>
  );
}

function MemoriaDrawer({
  scope,
  entita,
  onClose,
}: {
  scope: ScopeTipo;
  entita: EntitaMemoria;
  onClose: () => void;
}) {
  const regole = useMemoriaRegole(scope, entita.scope_ref);
  const conferma = useAzioneRegola("conferma");
  const revoca = useAzioneRegola("revoca");
  const chat = useMemoriaChat();
  const [testo, setTesto] = useState("");
  const [convId, setConvId] = useState<string | null>(null);
  const [messaggi, setMessaggi] = useState<{ role: "user" | "aida"; testo: string }[]>([
    {
      role: "aida",
      testo: `Dimmi una particolarità su «${entita.etichetta}» e la trasformo in una regola chiara. Ti chiederò se vale sempre o fino a una data.`,
    },
  ]);

  const invia = () => {
    const t = testo.trim();
    if (!t || chat.isPending) return;
    setMessaggi((m) => [...m, { role: "user", testo: t }]);
    setTesto("");
    chat.mutate(
      { scope_tipo: scope, scope_ref: entita.scope_ref, conversazione_id: convId, messaggio: t },
      {
        onSuccess: (res) => {
          setConvId(res.conversazione_id);
          setMessaggi((m) => [...m, { role: "aida", testo: res.risposta }]);
        },
        onError: (e) =>
          setMessaggi((m) => [...m, { role: "aida", testo: `(errore: ${(e as Error).message})` }]),
      }
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/35" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-[min(920px,97vw)] flex-col border-l bg-background shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b p-4">
          <div>
            <h2 className="text-lg font-semibold leading-tight">{entita.etichetta}</h2>
            <p className="mt-1 text-xs text-muted-foreground">{entita.sottotitolo}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground">
            <X className="size-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* SINISTRA — regole (scroll) */}
          <div className="w-[42%] shrink-0 space-y-2.5 overflow-y-auto border-r p-4 text-[13px]">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Regole
            </p>
            {regole.isLoading ? (
              <Loader2 className="mx-auto size-5 animate-spin text-muted-foreground" />
            ) : (
              <>
                {(regole.data?.correnti ?? []).map((r) => (
                  <RegolaCard
                    key={r.id}
                    r={r}
                    onConferma={r.stato === "proposta" ? () => conferma.mutate(r.id) : undefined}
                    onRevoca={() => revoca.mutate(r.id)}
                  />
                ))}
                {!regole.data?.correnti.length && (
                  <p className="py-6 text-center text-xs text-muted-foreground">
                    Nessuna regola ancora. Insegnane una ad Aida qui a fianco.
                  </p>
                )}
                {!!regole.data?.ereditate.length && (
                  <>
                    <p className="mt-4 text-xs font-medium text-muted-foreground">Ereditate</p>
                    {regole.data.ereditate.map((r) => (
                      <RegolaCard key={r.id} r={r} />
                    ))}
                  </>
                )}
                {!!regole.data?.storico.length && (
                  <>
                    <p className="mt-4 text-xs font-medium text-muted-foreground">Storico</p>
                    {regole.data.storico.map((r) => (
                      <RegolaCard key={r.id} r={r} />
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          {/* DESTRA — chat con Aida a tutta altezza */}
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-2.5 overflow-y-auto p-4 text-sm">
              {messaggi.map((m, i) => (
                <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                  <div
                    className={cn(
                      "max-w-[85%] rounded-lg px-3 py-2 leading-snug",
                      m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}
                  >
                    {m.role === "aida" ? <MiniMarkdown text={m.testo} /> : m.testo}
                  </div>
                </div>
              ))}
              {chat.isPending && (
                <div className="flex justify-start">
                  <div className="rounded-lg bg-muted px-3 py-2">
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 border-t p-3">
              <input
                value={testo}
                onChange={(e) => setTesto(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && invia()}
                placeholder="Insegna una regola ad Aida…"
                className="flex-1 rounded-md border bg-muted px-3 py-2 text-sm focus:bg-background"
              />
              <button
                type="button"
                onClick={invia}
                disabled={chat.isPending}
                className="grid size-9 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground disabled:opacity-50"
              >
                <Send className="size-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function RegolaCard({
  r,
  onConferma,
  onRevoca,
}: {
  r: MemoriaRegola;
  onConferma?: () => void;
  onRevoca?: () => void;
}) {
  const dot = r.ereditata
    ? "bg-cyan-500"
    : r.azionabile
      ? "bg-primary"
      : "bg-slate-400";
  const spento = r.stato === "scaduta" || r.stato === "revocata" || r.stato === "superseded";
  return (
    <div className={cn("rounded-lg border p-3.5", spento && "opacity-60")}>
      <div className="flex items-start gap-2.5">
        <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", dot)} />
        <div className="min-w-0 flex-1">
          <div className="text-sm leading-snug">{r.testo}</div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="rounded-full bg-secondary px-2 py-0.5 font-medium">
              {r.tipo_etichetta}
              {r.azionabile && !r.ereditata ? " · incide sul carico" : ""}
            </span>
            {r.ereditata && (
              <span className="rounded-full bg-cyan-50 px-2 py-0.5 text-cyan-700">
                ereditata ({r.scope_tipo})
              </span>
            )}
            <span className="rounded-full bg-secondary px-2 py-0.5">{validitaLabel(r)}</span>
            <span className="rounded-full bg-secondary px-2 py-0.5">{STATO_LABEL[r.stato]}</span>
            {r.provenienza && <span className="text-muted-foreground">da {r.provenienza}</span>}
          </div>
          {(onConferma || onRevoca) && (
            <div className="mt-2 flex gap-2">
              {onConferma && (
                <button
                  type="button"
                  onClick={onConferma}
                  className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
                >
                  Conferma
                </button>
              )}
              {onRevoca && (
                <button
                  type="button"
                  onClick={onRevoca}
                  className="rounded-md border px-2.5 py-1 text-xs text-muted-foreground hover:text-destructive"
                >
                  Revoca
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
